<template>
    <PerfectScrollbar>
        <v-timeline>
            <v-timeline-item
                v-for="(item, i) in messages"
                :key="i"
                :left="item.direction === 'in'"
                :right="item.direction === 'out'"
                :icon="getStatusIcon(item.status)"
                :color="getStatusColor(item.status)"
            >
                <div v-if="item.direction === 'out' && item.whatsAppPhoneNumber" slot="opposite" class="d-flex align-center justify-end">
                    <div class="d-flex flex-column">
                        <span>
                            <span class="grey--text text--darken-4 font-weight-bold">{{ item.userName }}</span>
                            <span class="ml-2 grey--text text--darken-2">{{ formatTimestamp(item.timestamp) }}</span>
                        </span>
                        <span class="mt-1 grey--text text--darken-1">
                            {{ item.whatsAppPhoneNumber }}
                        </span>
                    </div>
                    <v-icon class="ml-2">mdi-whatsapp</v-icon>
                    <v-btn icon small @click="copyToClipboard(item.text)" class="ml-2"><v-icon>mdi-clipboard-file-outline</v-icon></v-btn>
                    <v-btn icon small @click="resend(item.text)" class="ml-2"><v-icon>mdi-share-outline</v-icon></v-btn>
                </div>
                <div v-else-if="item.direction === 'out' && item.email" slot="opposite" class="d-flex align-center justify-end">
                    <div class="d-flex flex-column">
                        <span>
                            <span class="grey--text text--darken-4 font-weight-bold">{{ item.userName }}</span>
                            <span class="ml-2 grey--text text--darken-2">{{ formatTimestamp(item.timestamp) }}</span>
                        </span>
                        <span class="mt-1 grey--text text--darken-1">
                            {{ item.email }}
                        </span>
                    </div>
                    <v-icon class="ml-2">mdi-email-outline</v-icon>
                    <v-btn icon small @click="copyToClipboard(item.text)" class="ml-2"><v-icon>mdi-clipboard-file-outline</v-icon></v-btn>
                    <v-btn icon small @click="resend(item.text)" class="ml-2"><v-icon>mdi-share-outline</v-icon></v-btn>
                </div>
                <v-card class="elevation-2 blue lighten-4">
                    <v-card-text class="message" :class="{ 'html-message': isHtmlMessage(item.text) }" v-html="transformMessage(item.text)"></v-card-text>
                </v-card>
            </v-timeline-item>
        </v-timeline>
    </PerfectScrollbar>
</template>

<style scoped lang="scss">
.ps {
    height: 100%;
    padding-left: 8px;
    padding-right: 8px;
}

.message {
    white-space: pre-wrap;
}
.html-message {
    zoom: 0.6;
    overflow-x: hidden;
    overflow-y: hidden;
    max-height: 500px;
    white-space: normal;
}
</style>

<script lang="ts">
import { EventBus } from "@/components/EventBus";
import { OpenCrmRequest, DataClient } from "@/services/DataClient";
import { GetConversationResponse } from "@/services/ClientInterfaces";
import { Component, Prop, Vue, Watch } from "vue-property-decorator";
import { BaseView } from "@/components/BaseView";
import { PerfectScrollbar } from "vue2-perfect-scrollbar";
import { Utilities } from "@/services/Utilities";

@Component
export default class extends BaseView {
    @Prop({ required: true, type: String })
    conversationId!: string;

    @Prop({ required: false, default: 0, type: Number })
    refreshToken!: number;

    conversation: GetConversationResponse = {
        id: "",
        messages: [],
        creationDate: "",
    };

    get messages() {
        return this.conversation.messages.slice().reverse();
    }

    async mounted() {
        this.fetchData();
    }

    @Watch("refreshToken")
    @Watch("conversationId")
    async onRefreshTokenChanged() {
        await this.fetchData();
    }

    async fetchData() {
        if (this.conversationId) {
            this.conversation = await DataClient.Conversations.get({ id: this.conversationId });
        } else {
            this.conversation = {
                id: "",
                messages: [],
                creationDate: "",
            };
        }
    }

    // transform the message string to a html string, adding <a> tags to the links
    transformMessage(message: string) {
        const urlRegex = /(https?:\/\/[^\s]+)/g;
        return message.replace(urlRegex, (url) => {
            return '<a href="' + url + '" target="_blank">' + url + "</a>";
        });
    }

    formatTimestamp(timestamp: string) {
        const date = timestamp.substring(0, 10);
        return `${Utilities.getDayOfWeekNameYYYYMMDD(date)}, ${Utilities.formatTimestamp(timestamp)}`;
    }

    isHtmlMessage(message: string) {
        return message.indexOf("<!") >= 0;
    }

    copyToClipboard(text: string) {
        Utilities.copyToClipboardWithFeedback(text);
    }

    resend(text: string) {
        this.$emit("resend", text);
    }

    getStatusIcon(status: string) {
        switch (status) {
            case "sent":
                return "mdi-check";
            case "pending":
                return "mdi-timer-outline";
            case "received":
                return "mdi-check";
            case "failed":
                return "mdi-alert-circle-outline";
            default:
                return "";
        }
    }

    getStatusColor(status: string) {
        switch (status) {
            case "sent":
                return "success";
            case "pending":
                return "orange darken-2";
            case "received":
                return "success";
            case "failed":
                return "error";
            default:
                return "";
        }
    }
}
</script>
